import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AmountStrategyCellFragment,
  EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment,
  EmployeePayrollRunsQuery,
  FixedAmountStrategyCellFragment,
  PayrollValueObjectsConceptUnitTypeEnum,
  VariableAmountStrategyCellFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { type UnitsConfig } from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

// TODO: check if we can potentially remove this type
export type EmployeePayrollRunNode = NonNullable<
  NonNullable<EmployeePayrollRunsQuery['compensations']>['payrollRunEmployeesConnection']
>['nodes'][number]

export const formatCellValue = (
  unit: PayrollValueObjectsConceptUnitTypeEnum,
  unitSymbol: string,
  amount: number,
  locale: string
) => {
  if (unit === 'money') {
    return MoneyWithCurrency({
      optionalDecimals: true,
      amount,
      currency: unitSymbol,
      locale,
    })
  }

  if (unit === 'time') {
    const hours = Math.floor(amount / 60)
    const minutes = Math.round(amount % 60)

    if (hours === 0 && minutes === 0) {
      return '0h'
    } else if (hours === 0 && minutes > 0) {
      return `${minutes}m`
    } else if (minutes === 0) {
      return `${hours}h`
    } else {
      return `${hours}h ${minutes}m`
    }
  }

  if (unit === 'distance' || unit === 'unit') {
    if (Number.isInteger(amount)) {
      return `${amount} ${unitSymbol}`
    } else {
      return `${amount} ${unitSymbol}`
    }
  }

  return `${amount} ${unitSymbol}`
}

type VariableAmountStrategyCellProps = {
  amountStrategy: VariableAmountStrategyCellFragment
  unit: PayrollValueObjectsConceptUnitTypeEnum
  unitSymbol: string
  locale: string
}

function variableAmountStrategyCellText({
  amountStrategy,
  unit,
  unitSymbol,
  locale,
}: VariableAmountStrategyCellProps) {
  const { amount, upperLimit } = amountStrategy

  const isAmountNotDefined = amount === undefined || amount === null

  if (isAmountNotDefined && !upperLimit) {
    return { text: undefined, placeholder: i18n.t('compensations.common.noLimit') }
  }

  if (isAmountNotDefined && upperLimit) {
    return {
      text: undefined,
      placeholder: `${i18n.t('compensations.common.upTo')} ${formatCellValue(
        unit,
        unitSymbol,
        upperLimit ?? 0,
        locale
      )}`,
    }
  }

  return formatCellValue(unit, unitSymbol, amount ?? 0, locale)
}

const isFixedAmountStrategyCell = (
  amountStrategy: AmountStrategyCellFragment
): amountStrategy is FixedAmountStrategyCellFragment =>
  amountStrategy.__typename === 'CompensationsFixedAmountStrategy'
const isVariableAmountStrategyCell = (
  amountStrategy: AmountStrategyCellFragment
): amountStrategy is FixedAmountStrategyCellFragment =>
  amountStrategy.__typename === 'CompensationsVariableAmountStrategy'

type EmployeePayrollRunElementCellProps = {
  amountStrategy: AmountStrategyCellFragment
  unit: PayrollValueObjectsConceptUnitTypeEnum
  unitSymbol: string
  locale: string
}

function employeePayrollRunElementCell({
  amountStrategy,
  unit,
  unitSymbol,
  locale,
}: EmployeePayrollRunElementCellProps): RenderType {
  if (isFixedAmountStrategyCell(amountStrategy)) {
    return {
      type: 'text',
      value: formatCellValue(unit, unitSymbol, amountStrategy.amount ?? 0, locale),
    }
  }

  if (isVariableAmountStrategyCell(amountStrategy)) {
    return {
      type: 'text',
      value: variableAmountStrategyCellText({ amountStrategy, unit, unitSymbol, locale }),
    }
  }

  return {
    type: 'text',
    value: '-',
  }
}

type EmployeePayrollRunElementProps = {
  employeePayrollElement?: EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment
  locale: string
  unitsConfiguration: UnitsConfig
}

export function employeePayrollRunElement({
  employeePayrollElement,
  locale,
  unitsConfiguration,
}: EmployeePayrollRunElementProps): RenderType {
  return employeePayrollElement?.compensationsAmountStrategy &&
    employeePayrollElement.payrollConcept?.unitType
    ? employeePayrollRunElementCell({
        amountStrategy: employeePayrollElement.compensationsAmountStrategy,
        unit: employeePayrollElement.payrollConcept.unitType,
        unitSymbol: unitsConfiguration[employeePayrollElement.payrollConcept?.unitType],
        locale,
      })
    : {
        type: 'text',
        value: '-',
      }
}

type OneDataCollectionProps = React.ComponentProps<typeof OneDataCollection>
type Visualizations = OneDataCollectionProps['visualizations']
type TableVisualization = Extract<Visualizations[number], { type: 'table' }>
export type ColumnsType = TableVisualization['options']['columns'][number]
export type RenderType = ReturnType<ColumnsType['render']>
export type { EmployeePayrollRunElementProps }
